import {
  // queryProducts,
  queryShopPolicy,
  queryProduct,
  queryCart,
  mutateCreateCart,
  mutateCartLinesAdd,
  mutateCartLinesUpdate,
  mutateCartLinesRemove
} from './shopify-graphql'

// Shopify retired the Checkout API (Storefront API 2022-10+); a "Cart" is its replacement and plays the
// same role. State/action/mutation names below still say "checkout" to avoid touching every call site
// across Cart.vue/Product.vue/App.vue for a same-behaviour API swap - `state.checkout` now holds a Cart.
export default {
  namespaced: true,
  state: {
    checkout: null,
    checkoutUpdating: false
  },
  getters: {
    cartCount (state) {
      return !state.checkout ? null : state.checkout.totalQuantity
    },
    price: (state, getters) => (amnt, clean = true) => {
      if (isNaN(amnt)) return 'N/A'
      amnt = '£' + commaNum(Number(amnt).toFixed(2).toString())
      return !clean ? amnt : amnt.replace('.00', '')
    }
  },
  mutations: {
    setCheckoutUpdating (state, bool = true) {
      state.checkoutUpdating = bool
    },
    setCheckout (state, cart) {
      // reshape to look like the old Checkout the views read (Cart.vue: cart.webUrl, cart.subtotalPriceV2,
      // item.variant.*, item.customAttributes) so this API swap needed no changes there
      cart.webUrl = cart.checkoutUrl
      cart.subtotalPriceV2 = cart.cost.subtotalAmount
      cart.totalPriceV2 = cart.cost.totalAmount
      cart.lineItems = cart.lines.edges.map(({ node }) => ({
        id: node.id,
        quantity: node.quantity,
        unitPrice: node.cost.amountPerQuantity,
        variant: node.merchandise,
        title: node.merchandise?.product?.title,
        customAttributes: node.attributes
      }))
      state.checkout = cart
      localStorage.setItem('cartId', cart.id)
    },
    clearCheckout (state) {
      state.checkout = null
    }
  },
  actions: {
    // async getProducts ({ getters }, { first = 160, query, after, fullBody = false }) {
    //   // query = query && 'query=' + query // add query prefix if set
    //   const qry = {
    //     query: queryProducts(fullBody),
    //     variables: { first, query, after }
    //   }
    //   const resp = await shopify(qry).catch(onErr)
    //   return resp?.data?.products
    // },

    async getShopPolicy (_, policyHandle) {
      const resp = await shopify({ query: queryShopPolicy(policyHandle) }).catch(onErr)
      return resp.data.shop[policyHandle]
    },

    async getProduct (_, handle) {
      const qry = {
        query: queryProduct,
        variables: { handle }
      }
      const resp = await shopify(qry).catch(onErr)
      return resp?.data?.productByHandle
    },

    async fetchCheckout ({ commit }) {
      const id = localStorage.getItem('cartId')
      if (!id) return null
      const qry = {
        query: queryCart,
        variables: { id }
      }
      const resp = await shopify(qry).catch(onErr)
      if (resp.errors) {
        console.error(resp.errors)
        return Promise.reject(new Error('fetchCheckout query had an error'))
      }
      const cart = resp?.data?.cart
      // errors ?
      const reset = !cart ? 'Missing cart'
        : cart.lines?.edges?.find(itm => !itm.node?.merchandise) ? 'Cart contains deleted products'
          : null
      // reset
      if (reset) {
        console.warn(reset, '- Destroying saved cart')
        localStorage.removeItem('cartId')
        commit('clearCheckout') // reset
        return null
      }
      // save
      commit('setCheckout', cart)
      return cart
    },

    async newCheckout ({ commit }, lineItems) {
      const qry = {
        query: mutateCreateCart,
        variables: { lines: lineItems.map(toCartLineInput) }
      }
      commit('setCheckoutUpdating')
      const resp = await shopify(qry).catch(onErr)
      if (resp?.data?.cartCreate?.userErrors?.length) onErr(resp.data.cartCreate.userErrors)
      const cart = resp?.data?.cartCreate?.cart
      if (cart) {
        commit('setCheckout', cart)
        return cart
      }
      commit('setCheckoutUpdating', false)
      return false
    },

    // lineItems is the FULL desired set of {variantId, quantity, customAttributes} -
    // diffed against the current cart and applied as add/update/remove calls (the Cart API
    // has no single "replace all lines" mutation like the old Checkout API did).
    async updateCheckoutItems ({ state, commit, dispatch }, lineItems) {
      if (!state.checkout) return dispatch('newCheckout', lineItems)
      const cartId = state.checkout.id
      const existing = state.checkout.lineItems // [{ id, variant: { id }, ... }]
      const desiredByVariant = new Map(lineItems.map(itm => [itm.variantId, itm]))
      const existingByVariant = new Map(existing.map(itm => [itm.variant.id, itm]))

      const toRemove = existing.filter(itm => !desiredByVariant.has(itm.variant.id)).map(itm => itm.id)
      const toUpdate = lineItems
        .filter(itm => existingByVariant.has(itm.variantId))
        .map(itm => ({ id: existingByVariant.get(itm.variantId).id, quantity: itm.quantity, attributes: itm.customAttributes }))
      const toAdd = lineItems.filter(itm => !existingByVariant.has(itm.variantId)).map(toCartLineInput)

      commit('setCheckoutUpdating')
      let cart
      if (toRemove.length) {
        const resp = await shopify({ query: mutateCartLinesRemove, variables: { id: cartId, lineIds: toRemove } }).catch(onErr)
        if (resp?.data?.cartLinesRemove?.userErrors?.length) onErr(resp.data.cartLinesRemove.userErrors)
        cart = resp?.data?.cartLinesRemove?.cart
      }
      if (toUpdate.length) {
        const resp = await shopify({ query: mutateCartLinesUpdate, variables: { id: cartId, lines: toUpdate } }).catch(onErr)
        if (resp?.data?.cartLinesUpdate?.userErrors?.length) onErr(resp.data.cartLinesUpdate.userErrors)
        cart = resp?.data?.cartLinesUpdate?.cart || cart
      }
      if (toAdd.length) {
        const resp = await shopify({ query: mutateCartLinesAdd, variables: { id: cartId, lines: toAdd } }).catch(onErr)
        if (resp?.data?.cartLinesAdd?.userErrors?.length) onErr(resp.data.cartLinesAdd.userErrors)
        cart = resp?.data?.cartLinesAdd?.cart || cart
      }
      if (cart) commit('setCheckout', cart)
      commit('setCheckoutUpdating', false)
      return cart
    },

    async addItemToCheckout ({ state, dispatch }, { variantId, quantity, customAttributes }) {
      // no checkout? create new with items...
      if (!state.checkout) {
        return dispatch('newCheckout', [{ variantId, quantity, customAttributes }])
      }
      // check if variant exists in checkout...
      const newItems = state.checkout.lineItems.map(itm => ({ variantId: itm.variant.id, quantity: itm.quantity, customAttributes }))
      const i = newItems.findIndex(itm => itm.variantId === variantId)
      if (i > -1) {
        newItems[i].quantity += quantity // add quantity
      } else {
        newItems.push({ variantId, quantity, customAttributes })
      }
      return dispatch('updateCheckoutItems', newItems)
    }
  }
}

// helpers
const toCartLineInput = ({ variantId, quantity, customAttributes }) => ({ merchandiseId: variantId, quantity, attributes: customAttributes })

const shopify = ({ query, variables }) => {
  return fetch('https://' + process.env.VUE_APP_SHOPIFY_DOMAIN + '/api/2026-07/graphql', {
    method: 'POST',
    headers: {
      'X-Shopify-Storefront-Access-Token': process.env.VUE_APP_SHOPIFY_TOKEN,
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ query, variables })
  })
    .then(resp => resp.json())
    .then(data => {
      if (data.errors) {
        console.error(data.errors)
        return Promise.reject(new Error('Shopify API Response Errors'))
      }
      return data
    })
    .catch(e => console.error('Shopify API Error:', { error: e, query, variables }))
}
// error handler
const onErr = e => console.error('Shopify API Error:', e)

function commaNum (val) {
  while (/(\d+)(\d{3})/.test(val.toString())) {
    val = val.toString().replace(/(\d+)(\d{3})/, '$1' + ',' + '$2')
  }
  return val
}
